import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { get } from 'lodash-es';
import {
  logout,
  fetchUserInfoSuccess,
  fetchUserInfoFail,
  fetchUserInfoRequest,
  fetchAuthorizationInfoSuccess,
  fetchAuthorizationInfoFail,
  fetchAuthorizationLoginSuccess,
  fetchAuthorizationLoginFail,
  postResetPasswordSuccess,
  postResetPasswordFail,
  postChangePasswordSuccess,
  postChangePasswordFail,
} from './actions';
import {
  FETCH_USERINFO_REQUEST,
  FETCH_AUTHORIZATION_INFO_REQUEST,
  FETCH_AUTHORIZATION_LOGIN_REQUEST,
  POST_RESET_PASSWORD_REQUEST,
  POST_CHANGE_PASSWORD_REQUEST,
} from './actionTypes';
import { fetchUserLanguage } from '../System/actions';
import { fetchThemeSuccess } from '../Theme/actions';
import { getNavigatorLanguage, getOptions, processResponse } from '../../Utils/Utils';

const getUsersInfoUrl = '/users/info';
const authorizationInfoUrl = '/auth';
const coogLoginUrl = '/auth/coog';
const resetPasswordUrl = '/auth/coog/reset_password/';
const changePasswordUrl = '/auth/coog/reset_password/reset';

const credentials = action => {
  const { login, password } = action.credentials;
  return {
    username: login,
    password,
  };
};

// Moved api call into own function (for easy test swapping)
export function fetchFromApi(options) {
  return axios(options)
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

function* authorizationLogin(action) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({
      url: coogLoginUrl,
      method: 'POST',
      data: credentials(action),
    })
  );
  if (response) {
    yield put(fetchAuthorizationLoginSuccess(response));
    yield put(fetchUserInfoRequest());
  } else {
    yield put(fetchAuthorizationLoginFail(error));
  }
}

function* userinfo() {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: getUsersInfoUrl }));
  const { type, data, alerts } = processResponse({ response, error });

  switch (type) {
    case 'success':
      yield put(fetchUserInfoSuccess(data));
      yield put(fetchUserLanguage(data));
      yield put(fetchThemeSuccess({ data }));
      break;
    case 'fail':
      yield put(fetchUserLanguage({ pref: { language: getNavigatorLanguage().substr(0, 2) } }));
      yield put(fetchUserInfoFail(alerts));
      break;
    case 'forbidden':
      yield put(fetchUserLanguage({ pref: { language: getNavigatorLanguage().substr(0, 2) } }));
      yield put(logout({ alerts: ['error.login.account.forbidden'] }));
      break;
    case 'logout':
      yield put(fetchUserLanguage({ pref: { language: getNavigatorLanguage().substr(0, 2) } }));
      // yield put(logout());
      break;
    default:
      break;
  }
}

function* authorizationInfo() {
  const { response, error } = yield call(fetchFromApi, getOptions({ url: authorizationInfoUrl }));
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(fetchAuthorizationInfoSuccess({ data }));
      break;
    case 'fail':
      yield put(fetchAuthorizationInfoFail({ error: alerts }));
      break;
    case 'logout':
      yield put(logout());
      break;
    default:
      break;
  }
  return { response, error };
}

function* resetPassword(action) {
  const { response, error } = yield call(
    fetchFromApi,
    getOptions({
      url: resetPasswordUrl,
      method: 'POST',
      data: {
        login: action.login,
        context: { origin: `${window.location.href}` },
        reset_password_url: `${window.location.href}/change`,
      },
    })
  );
  const { type, data, alerts } = processResponse({ response, error });
  switch (type) {
    case 'success':
      yield put(postResetPasswordSuccess({ data }));
      break;
    case 'fail':
      yield put(postResetPasswordFail({ error: alerts }));
      break;
    default:
      break;
  }
  return { response, error };
}

function* changePassword(action) {
  let error;
  try {
    const responseData = yield call(
      fetchFromApi,
      getOptions({
        url: changePasswordUrl,
        method: 'POST',
        data: {
          reset_password_token: action.token,
          new_password: action.password,
        },
      })
    );
    let type;
    let response;

    if (get(responseData, 'error')) {
      type = 'fail';
      error = get(responseData, 'error.response.data');
    } else {
      type = 'success';
      response = get(responseData, 'response');
    }

    switch (type) {
      case 'success':
        yield put(postChangePasswordSuccess({ data: response.data }));
        break;
      case 'fail':
        yield put(postChangePasswordFail(error));
        break;
      default:
        break;
    }
    return { response, error };
  } catch (e) {
    error = { error: 'Internal error' };
    yield put(postChangePasswordFail(error));
    return error;
  }
}

function* watchAuth() {
  yield all([
    yield takeLatest(FETCH_USERINFO_REQUEST, userinfo),
    yield takeLatest(FETCH_AUTHORIZATION_INFO_REQUEST, authorizationInfo),
    yield takeLatest(FETCH_AUTHORIZATION_LOGIN_REQUEST, authorizationLogin),
    yield takeLatest(POST_RESET_PASSWORD_REQUEST, resetPassword),
    yield takeLatest(POST_CHANGE_PASSWORD_REQUEST, changePassword),
  ]);
}

export default watchAuth;
